import { useEffect, useRef, useState } from 'react'
import { useClickAnyWhere, useEventListener, useOnClickOutside } from 'usehooks-ts'
import DynamicAction from '@/components/ui/dynamic-action'
import { cx } from '@/utils/strings.ts'
import colors from '@/constants/colors'
import { TypeHeaderDropdownMenu } from '@/types/ctf'
import { buttonStyles } from './ui/cta-link'

type HeaderDropdownMenuProps = TypeHeaderDropdownMenu<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  /** Background color of parent header */
  headerBackgroundColor?: string
}

export default function HeaderDropdownMenu({
  title,
  links,
  headerBackgroundColor = colors.white,
}: HeaderDropdownMenuProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  useOnClickOutside(containerRef, () => {
    setOpen(false)
  })

  // Hide the outline only when the user clicks on the button
  const [hideOutline, setHideOutline] = useState(true)
  useClickAnyWhere((e) => {
    setHideOutline(true)
  })
  useEventListener('keydown', () => {
    setHideOutline(false)
  })

  return (
    <div ref={containerRef}>
      <button
        type="button"
        className={cx(
          'relative flex items-center gap-1 py-3 pr-1',
          headerBackgroundColor === colors.white && 'hover:text-indigoBlue',
          'dark:hover:text-primaryBlue',
          hideOutline && `outline-hidden`
        )}
        onClick={() => {
          setHideOutline(true)
          setOpen(!open)
        }}
      >
        <span className="text-base">{title}</span>
        <span className={cx('mt-[1px] ml-[1px] font-bwi text-base duration-150', open ? '-rotate-180' : 'rotate-0')}>
          &#xe92d;
        </span>
        {/* Animated border on open */}
        <div
          className={cx(
            'absolute bottom-2 left-0 w-full h-[2px] bg-[#487FE2] dark:bg-primaryBlue',
            'ease-[cubic-bezier(0.25,1,0.5,1)] transition-transform duration-300 scale-x-0 origin-left',
            open && 'scale-x-100'
          )}
        ></div>
      </button>
      <div
        className={cx(
          'absolute top-full translate-y-[1px] min-w-[21rem]',
          !open && 'pointer-events-none',
          'transition-opacity ease-[cubic-bezier(0.25,1,0.5,1)]',
          open ? 'opacity-100 duration-[500ms]' : 'opacity-0 duration-[400ms]'
        )}
        aria-hidden={!open}
      >
        <div
          className={cx(
            'rounded-b-lg border-x-[1px] border-b-[1px] border-secondary-100 dark:border-secondary-700 transform-gpu overflow-hidden',
            'bg-white dark:bg-secondary-800 origin-top transition-transform ease-[cubic-bezier(0.25,1,0.5,1)]',
            open ? 'scale-y-100 duration-[500ms]' : 'scale-y-50 duration-[300ms]'
          )}
        >
          <div
            className={cx(
              'grid m-2',
              'transition-transform ease-[cubic-bezier(0.25,1,0.5,1)]',
              open ? 'scale-y-100 duration-[500ms]' : 'scale-y-150 duration-[300ms]'
            )}
          >
            {links?.map((link) => {
              if (!link) return null
              return (
                <DynamicAction
                  key={link.sys.id}
                  className="dark:text-primaryBlue! dark:hover:text-indigoBlue! flex gap-1 py-2 px-6 duration-200 hover:bg-secondary-50 rounded-md"
                  {...(link.fields as any)}
                  style={buttonStyles.btnText}
                  tabIndex={!open && -1}
                />
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
